import { useEffect, useMemo, useRef, useState } from 'react';
import { Column, Row } from '@/components/SettingsLayout';
import { Modal } from '@/components/Modal/Modal';
import { Text, Icon, IconType, Input, Button } from '@/ui';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/Table';
import { useFormContext, useFieldArray, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { NUMBER_WITH_DECIMATES_VALIDATION_PATTERN } from '@/constants/constants';
import {
  COUNTRY_CURRENCIES,
  CurrencyCode,
  getCurrencyConfig,
  fromMinorUnits,
} from '@final-commerce/common';
import { SUPPORTED_PRICING_COUNTRIES } from '@/constants/pricingCountries';
import CountrySelect, { buildCountryOptions } from '@/components/CountrySelect/CountrySelect';
import {
  PricingChannel,
  PricingConfigurationValues,
  PricingValues,
} from '@/services/Pricing/pricing-configuration.service';
import PricingChannelTabs from './PricingChannelTabs';
import {
  FIELD_KEYS,
  FieldKey,
  FormRow,
  computeMargin,
  displayFieldValue,
  fixedMinorToDisplay,
  formatPercent,
  normalizeBlur,
  onPercentKeyDown,
  validateAgainstMaximum,
  validateAgainstMinimum,
} from './pricingFormMapping';

export type PricingFormMode = 'org' | 'plan' | 'minimum' | 'company';

type Props = {
  mode: PricingFormMode;
  defaults?: PricingConfigurationValues;
  minimums?: PricingConfigurationValues;
  orgPricing?: PricingConfigurationValues;
  // Saved overrides for this entity. The active country's row is seeded from here
  // when present, falling back to `defaults` (the inherited baseline) otherwise.
  existingValues?: PricingConfigurationValues;
  isReadOnly?: boolean;
  activeCountry: string;
  onActiveCountryChange: (code: string) => void;
  // The maps above are already narrowed to this channel by the container.
  channel: PricingChannel;
  onChannelChange: (channel: PricingChannel) => void;
  // Bumped by `usePricingForm` on a confirmed save; re-seeding on it clears `isDirty`.
  savedAt?: number;
};

const MarginCell = ({
  index,
  field,
  countryCode,
  orgValue,
  fixedSuffix,
}: {
  index: number;
  field: FieldKey;
  countryCode: string;
  orgValue: number | undefined;
  fixedSuffix: string;
}) => {
  const watched = useWatch({ name: `rows.${index}.${field}` });
  return (
    <Text size='s' weight='light'>
      {computeMargin(watched, orgValue, field, countryCode, fixedSuffix)}
    </Text>
  );
};

const PricingConfigurationForm = ({
  mode,
  defaults,
  minimums,
  orgPricing,
  existingValues,
  isReadOnly,
  activeCountry,
  onActiveCountryChange,
  channel,
  onChannelChange,
  savedAt = 0,
}: Props) => {
  const { t } = useTranslation();
  const {
    control,
    register,
    reset,
    formState: { errors, isDirty },
  } = useFormContext<{ channel: PricingChannel; rows: FormRow[] }>();
  const { fields } = useFieldArray({
    control,
    name: 'rows',
  });

  const buildRow = (code: string): FormRow => {
    const seed: PricingValues = existingValues?.[code] ??
      defaults?.[code] ?? { variable: 0, fixed: 0, amex: 0 };
    return {
      code,
      variable: formatPercent(seed.variable),
      fixed: fixedMinorToDisplay(seed.fixed, code),
      amex: formatPercent(seed.amex),
    };
  };

  const seedRow = activeCountry ? buildRow(activeCountry) : null;
  // Re-seed on the row's contents, not the maps' identity: a container that skips `useMemo` would
  // otherwise reset the form on every keystroke.
  const seedKey = seedRow
    ? `${seedRow.code}|${seedRow.variable}|${seedRow.fixed}|${seedRow.amex}`
    : '';
  // Held in a ref so the effect can install the row without taking a dependency on its identity.
  const latestSeedRow = useRef(seedRow);
  latestSeedRow.current = seedRow;

  useEffect(() => {
    reset({ channel, rows: latestSeedRow.current ? [latestSeedRow.current] : [] });
  }, [channel, seedKey, savedAt, reset]);

  // A save covers one channel, so switching tabs discards unsaved edits — confirm first.
  const [pendingChannel, setPendingChannel] = useState<PricingChannel | null>(null);

  const handleChannelChange = (next: PricingChannel) => {
    if (next === channel) return;
    if (isDirty) {
      setPendingChannel(next);
      return;
    }
    onChannelChange(next);
  };

  const confirmChannelChange = () => {
    if (pendingChannel) onChannelChange(pendingChannel);
    setPendingChannel(null);
  };

  const configuredCodes =
    existingValues && Object.keys(existingValues).length > 0
      ? Object.keys(existingValues)
      : Object.keys(defaults ?? {});
  const unConfiguredCodes = SUPPORTED_PRICING_COUNTRIES.filter((c) => !configuredCodes.includes(c));
  const countryOptions = useMemo(
    () => buildCountryOptions(isReadOnly ? unConfiguredCodes : []),
    [isReadOnly, unConfiguredCodes],
  );

  const handleCountrySelect = (code: string) => {
    if (!code) return;
    onActiveCountryChange(code);
  };

  const showMinimumColumn = mode === 'org';
  const showMarginColumn = mode === 'plan' || mode === 'company';
  const floorErrorKey =
    mode === 'plan' || mode === 'company'
      ? t('Form.errors.pricing_lower_than_organization')
      : t('Form.errors.pricing_lower_than_minimal');
  const aboveDefaultErrorKey = t('Form.errors.minimal_pricing_bigger_than_default');
  const enforceDefaultCeiling = mode === 'minimum';
  const inputColumnLabel = (
    {
      plan: t('Pricing.plan_pricing') ?? 'Plan pricing',
      minimum: t('Pricing.minimum_pricing') ?? 'Minimum pricing',
      company: t('Pricing.pricing_input') ?? 'Pricing',
      org: t('Pricing.organization_pricing') ?? 'Organization pricing',
    } satisfies Record<PricingFormMode, string>
  )[mode];
  const baselineColumnLabel = (
    {
      plan: t('Pricing.organization_pricing') ?? 'Organization pricing',
      company: t('Pricing.organization_pricing') ?? 'Organization pricing',
      minimum: t('Pricing.default_pricing') ?? 'Default pricing',
      org: t('Pricing.default_pricing') ?? 'Default pricing',
    } satisfies Record<PricingFormMode, string>
  )[mode];

  return (
    <Column gapSize='24'>
      {/* Carried in the form data, so a save lands on the channel whose inputs were rendered even if
          a prop and the URL disagree for one render. */}
      <input type='hidden' {...register('channel' as const)} />
      <PricingChannelTabs channel={channel} onChange={handleChannelChange} />
      <Modal open={pendingChannel !== null} closeModal={() => setPendingChannel(null)}>
        <Modal.Title title={t('Pricing.channel_switch_discard_title')} />
        <Modal.Content border={true}>
          <Text size='m' weight='light-medium'>
            {t('Pricing.channel_switch_discard_message')}
          </Text>
        </Modal.Content>
        <Modal.Footer justify='flex-end'>
          <Row gapSize='8' fullWidth={false}>
            <Button variant='cancel' onClick={() => setPendingChannel(null)}>
              {t('Pricing.channel_switch_keep_editing')}
            </Button>
            <Button variant='primary' onClick={confirmChannelChange} testId='pricing-channel-discard'>
              {t('Pricing.channel_switch_discard')}
            </Button>
          </Row>
        </Modal.Footer>
      </Modal>
      {(fields.length > 0 || !isReadOnly) && (
        <Row gapSize='12' align='center' fullWidth={false}>
          <CountrySelect
            value={activeCountry}
            onChange={handleCountrySelect}
            options={countryOptions}
            disabled={isReadOnly && configuredCodes.length === 0}
            label={t('Pricing.select_country') ?? 'Select a country'}
          />
        </Row>
      )}

      {fields.length === 0 ? (
        <Text size='s' weight='light' color='base-grey'>
          {mode === 'minimum'
            ? (t('Pricing.no_minimums_configured') ?? 'No country floors configured yet.')
            : (t('Pricing.no_overrides_configured') ??
              'No country overrides yet — defaults will be applied.')}
        </Text>
      ) : (
        fields.map((field, index) => {
          const row = field;
          const code = row.code;
          const floorValues =
            mode === 'org'
              ? minimums?.[code]
              : mode === 'plan' || mode === 'company'
                ? orgPricing?.[code]
                : undefined;
          const def = defaults?.[code];
          const currencyCode = COUNTRY_CURRENCIES[code];
          const fixedSuffix = currencyCode
            ? getCurrencyConfig(currencyCode as CurrencyCode).symbol || currencyCode
            : '';
          const rowErrors = (errors.rows as any)?.[index];

          return (
            <Column key={field.id} gapSize='8'>
              <input type='hidden' {...register(`rows.${index}.code` as const)} />
              <Table data-testid={`pricing-${code}-table`}>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <Text size='s' weight='semi-bold'>
                        {t('Pricing.description')}
                      </Text>
                    </TableHead>
                    {showMinimumColumn && (
                      <TableHead>
                        <Text size='s' weight='semi-bold'>
                          {t('Pricing.minimum_allowed')}
                        </Text>
                      </TableHead>
                    )}
                    <TableHead>
                      <Text size='s' weight='semi-bold'>
                        {inputColumnLabel}
                      </Text>
                    </TableHead>
                    <TableHead>
                      <Text size='s' weight='semi-bold'>
                        {baselineColumnLabel}
                      </Text>
                    </TableHead>
                    {showMarginColumn && (
                      <TableHead>
                        <Text size='s' weight='semi-bold'>
                          {t('Pricing.your_margin')}
                        </Text>
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {FIELD_KEYS.map((fieldName) => {
                    const isFixed = fieldName === 'fixed';
                    const suffix = isFixed ? fixedSuffix : '%';
                    const fieldError = rowErrors?.[fieldName];
                    const floor = floorValues?.[fieldName];
                    const floorForCompare =
                      isFixed && floor !== undefined && currencyCode
                        ? fromMinorUnits(floor, currencyCode as CurrencyCode)
                        : floor;
                    const ceiling = enforceDefaultCeiling ? def?.[fieldName] : undefined;
                    const ceilingForCompare =
                      isFixed && ceiling !== undefined && currencyCode
                        ? fromMinorUnits(ceiling, currencyCode as CurrencyCode)
                        : ceiling;

                    return (
                      <TableRow key={`${code}-${fieldName}`}>
                        <TableCell>
                          <Column gapSize='4'>
                            <Text size='s' weight='light-medium'>
                              {t(`Pricing.${fieldName}`)}
                            </Text>
                            <Text size='s' weight='light' color='base-grey'>
                              {t(`Pricing.${fieldName}_comment`)}
                            </Text>
                          </Column>
                        </TableCell>
                        {showMinimumColumn && (
                          <TableCell>
                            <Text size='s' weight='light-medium'>
                              {displayFieldValue(floor, fieldName, code, fixedSuffix)}
                            </Text>
                          </TableCell>
                        )}
                        <TableCell>
                          <Column gapSize='4'>
                            <Input
                              disabled={isReadOnly}
                              variant='full'
                              suffix={suffix}
                              isError={!!fieldError}
                              onKeyDown={onPercentKeyDown}
                              {...register(`rows.${index}.${fieldName}` as const, {
                                required: t('Form.errors.required'),
                                pattern: {
                                  value: NUMBER_WITH_DECIMATES_VALIDATION_PATTERN,
                                  message: t('Form.errors.rate'),
                                },
                                validate: (val: string) => {
                                  const floorResult = validateAgainstMinimum(
                                    val,
                                    floorForCompare,
                                    !isFixed,
                                    floorErrorKey,
                                  );
                                  if (floorResult !== true) return floorResult;
                                  return validateAgainstMaximum(
                                    val,
                                    ceilingForCompare,
                                    !isFixed,
                                    aboveDefaultErrorKey,
                                  );
                                },
                                onBlur: normalizeBlur,
                              })}
                            />
                            {fieldError && (
                              <Row gapSize='4' align='center'>
                                <Icon type={IconType.Alert} width={16} height={16} />
                                <Text size='s' weight='light' color='red'>
                                  {fieldError.message}
                                </Text>
                              </Row>
                            )}
                          </Column>
                        </TableCell>
                        <TableCell>
                          <Text size='s' weight='light'>
                            {displayFieldValue(def?.[fieldName], fieldName, code, fixedSuffix)}
                          </Text>
                        </TableCell>
                        {showMarginColumn && (
                          <TableCell>
                            <MarginCell
                              index={index}
                              field={fieldName}
                              countryCode={code}
                              orgValue={orgPricing?.[code]?.[fieldName]}
                              fixedSuffix={fixedSuffix}
                            />
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Column>
          );
        })
      )}
    </Column>
  );
};

export default PricingConfigurationForm;
